import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Row Types (SQLite) ───

export interface InvoiceRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  appointment_id: string | null;
  consultation_id: string | null;
  queue_id: string | null;
  encounter_id: string | null;
  visit_id: string | null;
  invoice_no: string | null;
  patient_name: string | null;
  date_iso: string | null;
  status: string | null; // 'draft' | 'issued' | 'paid'
  reference_no: string | null;
  reference_type: string | null; // 'official' | 'unofficial'
  notes: string | null;
  subtotal: number | null;
  discount_total: number | null;
  tax_total: number | null;
  grand_total: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface InvoiceItemRow {
  id: string;
  org_id: string | null;
  invoice_id: string | null;
  service_name: string | null;
  service_id: string | null;
  qty: number | null;
  unit_price: number | null;
  discount: number | null;
  tax_rate: number | null;
  // PRD.190 — optional Medicine inventory reference + snapshots
  inventory_item_id: string | null;
  free_text_name: string | null;
  description_snapshot: string | null;
  sku_snapshot: string | null;
  unit_snapshot: string | null;
  unit_price_snapshot: number | null;
  tax_inclusive_snapshot: number | null; // 0/1
  open_pricing_snapshot: number | null; // 0/1
  source_type: string | null; // 'manual' | 'inventory_item' | 'prescription' | 'pharmacy_dispense' | 'procedure' | 'encounter'
  source_id: string | null;
  remarks: string | null;
  add_to_treatment: number | null; // 0/1
  sort_order: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface InvoicePaymentRow {
  id: string;
  org_id: string | null;
  invoice_id: string | null;
  amount_cents: number | null;
  method: string | null;
  notes: string | null;
  paid_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

// ─── Queries ───

export function qInvoicesByPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['invoices', 'by-patient', patientId] as const,
    sql: `SELECT * FROM invoices WHERE patient_id = ? ORDER BY created_at DESC`,
    params: [patientId],
    watch: ['invoices'],
  };
}

export function qInvoicesByEncounter(
  patientId: string,
  encounterId: string,
): QuerySpec<[string, string]> {
  return {
    key: ['invoices', 'by-encounter', patientId, encounterId] as const,
    sql: `SELECT * FROM invoices WHERE patient_id = ? AND encounter_id = ? ORDER BY created_at DESC`,
    params: [patientId, encounterId],
    watch: ['invoices'],
  };
}

// ─── Outstanding balance for a single patient ───

export interface PatientOutstandingRow {
  total_balance: number;
  unpaid_count: number;
  has_overdue: number; // 1 if any invoice > 30 days old
}

export function qPatientOutstandingBalance(patientId: string): QuerySpec<[string]> {
  return {
    key: ['invoices', 'outstanding', patientId] as const,
    sql: `SELECT
      COALESCE(SUM(grand_total), 0) AS total_balance,
      COUNT(*) AS unpaid_count,
      MAX(CASE WHEN julianday('now') - julianday(created_at) > 30 THEN 1 ELSE 0 END) AS has_overdue
    FROM invoices
    WHERE patient_id = ? AND status IN ('draft', 'issued')`,
    params: [patientId],
    watch: ['invoices'],
  };
}

export function qInvoiceItems(invoiceId: string): QuerySpec<[string]> {
  return {
    key: ['invoice_items', 'by-invoice', invoiceId] as const,
    sql: `SELECT * FROM invoice_items WHERE invoice_id = ? ORDER BY sort_order ASC, created_at ASC`,
    params: [invoiceId],
    watch: ['invoice_items'],
  };
}

// ─── PRD.216 — Global Invoice list rows ───
//
// Shared row shape for the operational Global Invoice module. Joins
// patient name (preferring the live patients.full_name when the
// denormalised invoices.patient_name is missing/empty) and derives
// paid_total + balance from invoice_payments so the queue can show
// remaining balance without a per-row follow-up query.

export interface GlobalInvoiceListRow {
  id: string;
  invoice_no: string | null;
  date_iso: string | null;
  status: string | null; // 'draft' | 'issued' | 'paid'
  reference_no: string | null;
  reference_type: string | null;
  patient_id: string | null;
  patient_name: string | null;
  encounter_id: string | null;
  grand_total: number | null;
  paid_total: number | null;
  balance: number | null;
  created_at: string | null;
  updated_at: string | null;
}

const GLOBAL_INVOICE_BASE_SELECT = `
  SELECT
    i.id,
    i.invoice_no,
    i.date_iso,
    i.status,
    i.reference_no,
    i.reference_type,
    i.patient_id,
    COALESCE(NULLIF(TRIM(i.patient_name), ''), p.full_name) AS patient_name,
    i.encounter_id,
    i.grand_total,
    COALESCE(
      (SELECT SUM(ip.amount_cents) / 100.0
         FROM invoice_payments ip
        WHERE ip.invoice_id = i.id),
      0
    ) AS paid_total,
    COALESCE(i.grand_total, 0) - COALESCE(
      (SELECT SUM(ip.amount_cents) / 100.0
         FROM invoice_payments ip
        WHERE ip.invoice_id = i.id),
      0
    ) AS balance,
    i.created_at,
    i.updated_at
  FROM invoices i
  LEFT JOIN patients p ON p.id = i.patient_id
`;

/**
 * PRD.216 — Ready to Settle queue (default tab on Global Invoice).
 *
 * Inclusion rule for v1: any invoice that is not yet 'paid'. Ordered
 * oldest-first so frontdesk works through aging balances naturally.
 * Cancelled/voided invoices (when introduced) should be excluded here.
 */
export function qInvoicesReadyToSettle(): QuerySpec<[]> {
  return {
    key: ['global-invoices', 'ready-to-settle'] as const,
    sql: `${GLOBAL_INVOICE_BASE_SELECT}
      WHERE i.status IN ('draft', 'issued')
      ORDER BY i.created_at ASC`,
    params: [],
    watch: ['invoices', 'patients', 'invoice_payments'],
  };
}

/**
 * PRD.216 — Invoice History (secondary tab on Global Invoice).
 *
 * All invoices ordered newest-first. v1 caps at 500 rows; the toolbar
 * search/filters narrow the displayed set. Pagination can come later.
 */
export function qGlobalInvoiceHistory(): QuerySpec<[]> {
  return {
    key: ['global-invoices', 'history'] as const,
    sql: `${GLOBAL_INVOICE_BASE_SELECT}
      ORDER BY i.created_at DESC
      LIMIT 500`,
    params: [],
    watch: ['invoices', 'patients', 'invoice_payments'],
  };
}

/**
 * PRD.216 — Operational KPIs for the Global Invoice queue strip.
 *
 * Returns aggregates the home Billing Snapshot doesn't surface:
 *   - Overdue: status IN ('draft','issued') older than 30 days. Same
 *     >30-day cutoff used by qPatientOutstandingBalance for parity.
 *   - Drafts: status = 'draft' regardless of age. Identifies invoices
 *     stuck before issue/payment.
 *
 * One row per query (single SELECT with conditional aggregates) so
 * all metrics share a single watcher and refresh together.
 */
export interface GlobalInvoiceOperationalKpisRow {
  overdue_total: number;
  overdue_count: number;
  draft_total: number;
  draft_count: number;
}

export function qGlobalInvoiceOperationalKpis(): QuerySpec<[]> {
  return {
    key: ['global-invoices', 'operational-kpis'] as const,
    sql: `SELECT
      COALESCE(SUM(CASE
        WHEN status IN ('draft', 'issued')
          AND julianday('now') - julianday(created_at) > 30
        THEN grand_total ELSE 0 END), 0) AS overdue_total,
      SUM(CASE
        WHEN status IN ('draft', 'issued')
          AND julianday('now') - julianday(created_at) > 30
        THEN 1 ELSE 0 END) AS overdue_count,
      COALESCE(SUM(CASE WHEN status = 'draft' THEN grand_total ELSE 0 END), 0) AS draft_total,
      SUM(CASE WHEN status = 'draft' THEN 1 ELSE 0 END) AS draft_count
    FROM invoices`,
    params: [],
    watch: ['invoices'],
  };
}
